/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { AngularFireAction, DatabaseSnapshot } from '@angular/fire/compat/database';
import { Project, ProjectFields } from 'src/app/app-data/models/Project';
import { BehaviorSubject, EMPTY, of } from 'rxjs';
import { FancyFirebaseSplitDataList } from '../generics/FancyFirebaseSplitDataList';
import { DefaultSplitDataRenderType } from '@touchplan/common';
import { RefOrObservable } from '../base/interfaces';
import { FancyAction } from '../base/FancyAction';
import { DefaultActionTypes, PersistDataAction } from '../generics/DefaultActionTypes';
import { DefaultActionHandlers } from '../generics/DefaultActionHandlers';
import { mergeMap, switchMap, tap } from 'rxjs/operators';
import { fancyFirebaseReferencer, fancyFirebaseGetters } from '@touchplan/fancy-referencer';
import firebase from 'firebase/compat/app';

export class ProjectList extends FancyFirebaseSplitDataList<
	ProjectFields,
	DefaultSplitDataRenderType,
	Project
> {
	public editList$: BehaviorSubject<Map<string, Project>> = new BehaviorSubject(new Map());

	//special handling for remove and persist
	protected defaultActionsHandlers: DefaultActionHandlers<Project, FancyAction, this> =
		new DefaultActionHandlers(this, [DefaultActionTypes.removeItem, DefaultActionTypes.persistData]);

	constructor(protected angularLand, path: RefOrObservable, fancyJoins) {
		super(angularLand, path, fancyJoins);
		this.logger.setContext('ProjectList');

		const ob$ = this.makeSimpleSublist(DefaultSplitDataRenderType.edit);
		this.subscriptions.push(ob$.subscribe(this.editList$));
	}

	public $$added(action: AngularFireAction<DatabaseSnapshot<any>>) {
		const project = new Project(action.payload.key);
		project.databaseUpdate(action.payload.val());
		return project;
	}

	public destroy() {
		this.editList$.complete();
		super.destroy();
	}

	// multi-partition specific overrides ---------------------

	public handleCustomEvents(baseObservable) {
		//run our overrides first
		const wrappedOb$ = baseObservable.pipe(
			switchMap((action: FancyAction<Project>) => {
				if (action.type === DefaultActionTypes.removeItem) {
					this.handleRemoveItem(action);
					return EMPTY; //we handled it
				} else if (action.type === DefaultActionTypes.persistData) {
					this.handlePersistData(action as PersistDataAction);
					return EMPTY; //we handled it
				}
				return of(action);
			})
		);
		return super.handleCustomEvents(wrappedOb$);
	}

	public generateNewChildKey() {
		//https://mocasystems.atlassian.net/browse/TP-2913
		throw new Error('ProjectList does not support generating child keys');
		return null; // eyeroll (typescript)
	}

	private handleRemoveItem(action: FancyAction<Project>) {
		this.$$removed(action.payload);
		this.syncList.delete(action.key);

		const path = fancyFirebaseGetters.getProjectsPath(action.key);
		const projectRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path, action.key);
		projectRef.remove().catch((e) => this.logger.error('Error deleting project: ', action.key, e));
	}

	private handlePersistData(action: PersistDataAction<Project>) {
		const project = action.payload;
		const dbOutput = project.generateDatabaseOutput(action.renderType as DefaultSplitDataRenderType);
		const path = fancyFirebaseGetters.getProjectsPath(action.key);
		const projectRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(path, action.key);
		projectRef.update(dbOutput);
		project.localDataNotYetPersisted = false;
	}
}
